import { Kbd } from "@prisma/ui";
import { Plus, Search } from "lucide-react";
import { useTranslation } from "react-i18next";

import { NotificationsBell } from "../components/NotificationsBell";
import { ThemeControl } from "../components/ThemeControl";
import { UserMenu } from "../components/UserMenu";
import { isMac } from "../lib/shortcuts";

const MOD = () => (isMac() ? "⌘" : "Ctrl");

/** Top bar in the locked order (ADR 0016 rule 8): search with ⌘K hint, theme control,
 *  New Creative with ⌘N, notifications, avatar with name and role. */
export function TopBar({ onSearch, onNewCreative }: { onSearch: () => void; onNewCreative: () => void }) {
  const { t } = useTranslation();
  return (
    <header className="flex h-[var(--topbar-height)] shrink-0 items-center gap-4 border-b border-line bg-app/80 px-8 backdrop-blur">
      <button
        type="button"
        onClick={onSearch}
        aria-keyshortcuts="Meta+K Control+K"
        className="flex h-11 max-w-[640px] flex-1 items-center gap-3 rounded-[var(--radius-card)] border border-line bg-surface px-4 text-left text-[14px] text-ink-2 hover:border-[var(--border-strong)]"
      >
        <Search aria-hidden="true" className="size-[18px]" />
        <span className="flex-1 truncate">{t("shell.search_placeholder")}</span>
        <span className="flex gap-1">
          <Kbd>{MOD()}</Kbd>
          <Kbd>{"K"}</Kbd>
        </span>
      </button>
      <div className="ml-auto flex items-center gap-3">
        <ThemeControl />
        <button
          type="button"
          onClick={onNewCreative}
          className="inline-flex h-11 shrink-0 items-center gap-2 whitespace-nowrap rounded-[var(--radius-card)] bg-[image:var(--gradient-primary-button)] px-5 text-[15px] font-medium text-[var(--text-on-accent)] shadow-[0_6px_20px_var(--accent-primary-soft)] hover:opacity-95"
        >
          <Plus aria-hidden="true" className="size-[18px]" />
          {t("shell.new_creative")}
          <span className="ml-1 flex gap-0.5 rounded-md bg-white/15 px-1.5 py-0.5 text-[11px]" aria-hidden="true">
            {MOD()} {"N"}
          </span>
        </button>
        <NotificationsBell />
        <UserMenu />
      </div>
    </header>
  );
}
